import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { BusinessUnitSummary } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface BusinessUnitSummariesState {
  status: Status;
  cursor?: string | null;
  links: { next: string | null };
}

const initialState: BusinessUnitSummariesState = {
  status: SLICE_STATUS.IDLE,
  cursor: '',
  links: { next: null },
};

const adapter = createEntityAdapter<BusinessUnitSummary>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.businessUnitSummaries;
  return status !== SLICE_STATUS.LOADING;
}

interface GetListParams {
  businessUnitIds?: string[];
  pageSize?: number;
  businessUnitName?: string;
}

interface GetBusinessUnitSummariesResponse {
  teams: BusinessUnitSummary[];
  links: { next: string };
}

const NO_OP_RESPONSE: GetBusinessUnitSummariesResponse = {
  teams: [],
  links: { next: '' },
};

export const getBusinessUnitSummaries = createAsyncThunk<
  GetBusinessUnitSummariesResponse,
  GetListParams,
  ThunkApi
>(
  'normalized/businessUnitSummary/getBusinessUnitSummaries',
  async (
    { businessUnitIds, pageSize, businessUnitName },
    { getState, extra: { getBusinessUnitSummariesApi } }
  ) => {
    const state = getState();

    const cleanedIds = (businessUnitIds ?? []).filter(
      (id): id is string => typeof id === 'string' && id.trim() !== ''
    );

    let notCached = cleanedIds;
    let params = new URLSearchParams();

    if (pageSize) {
      params.append('limit', pageSize.toString());
    }

    if (businessUnitName) {
      params.append('filter[businessUnitName]', businessUnitName);
    }

    if (cleanedIds.length > 0) {
      const uniqueBusinessUnitIds = new Set(cleanedIds);

      notCached = Array.from(uniqueBusinessUnitIds).filter(
        (id) => !state.normalized.businessUnitSummaries.entities[id]
      );

      notCached.forEach((id) => id && params.append('filter[ids][]', id));

      if (notCached.length === 0) {
        return NO_OP_RESPONSE;
      }
    }

    if (state.normalized.businessUnitSummaries.cursor) {
      params.append('after', state.normalized.businessUnitSummaries.cursor);
    }

    const { teams, links } = await getBusinessUnitSummariesApi().getList({
      params,
    });

    return { teams, links };
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const businessUnitSummariesSlice = createSlice({
  name: 'normalized/businessUnitSummary',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessUnitSummaries.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getBusinessUnitSummaries.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getBusinessUnitSummaries.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.teams);
        state.links = action.payload.links;
        const afterValue = action.payload.links?.next
          ? new URLSearchParams(action.payload.links.next.split('?')[1]).get(
              'after'
            )
          : null;
        state.cursor = afterValue;
        state.status = SLICE_STATUS.SUCCESS;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.businessUnitSummaries
);

const selectStatus = (state: RootState) =>
  state.normalized.businessUnitSummaries.status;

const selectLinksNext = (state: RootState) =>
  state.normalized.businessUnitSummaries.cursor;

export const businessUnitSummarySelectors = {
  ...defaultSelectors,
  selectLinksNext,
  selectStatus,
};

export default businessUnitSummariesSlice.reducer;
